import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { documentWorkflowControllerRemoveSubmittedFormMutation } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { getUserFacingErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import type { UseRemoveSubmittedFormOptions } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useRemoveSubmittedForm.types';

/** Withdraws a signable form the investor uploaded but has not signed. */
export const useRemoveSubmittedForm = ({
	programmeId,
	investorId,
	onRemoved,
}: UseRemoveSubmittedFormOptions) => {
	const tInvestor = useTranslationInvestor();

	const mutation = useMutation({
		...documentWorkflowControllerRemoveSubmittedFormMutation(),
		// Returned, not fired: the dialog closes by the row unmounting, so staying pending until
		// the refresh lands is what stops a second Confirm sending another DELETE.
		onSuccess: () => {
			showToastV2({
				state: 'success',
				title: tInvestor('investment.contracts.signableFormModal.remove_form_success'),
			});
			return onRemoved();
		},
		// The backend's reason is the useful part — "it must be rejected or reset" is what tells
		// the investor an admin is needed, which is the support round-trip this feature removes.
		onError: (error: Error) => {
			showToastV2({
				state: 'error',
				title:
					getUserFacingErrorMessage(error) ??
					tInvestor('investment.contracts.signableFormModal.remove_form_error'),
			});
		},
	});

	// Stable: the caller feeds this into the document table's column memo, which would otherwise
	// rebuild every render.
	const { mutate } = mutation;
	const removeSubmittedForm = useCallback(
		(documentWorkflowInstanceId: string) =>
			mutate({
				path: { programmeId, documentWorkflowInstanceId },
				query: { investorId },
			}),
		[mutate, programmeId, investorId]
	);

	return {
		removeSubmittedForm,
		/** The document currently being withdrawn — every other row stays usable. */
		removingInstanceId: mutation.isPending
			? mutation.variables?.path?.documentWorkflowInstanceId
			: undefined,
	};
};
